import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiClient, IframeUnopenedOffer } from '../../modules/api';
import { Backdrop } from '../../modules/design-system';
import { useShippingDisabled } from '../../modules/shipping';
import { useBackHandler } from '../../modules/backStack';
import { MyPacksHeader, type MyPacksTabDef, type MyPacksTabId } from '../MyPacks/MyPacksHeader';
import { MyPacksStatsRow } from '../MyPacks/MyPacksStatsRow';
import { MyPacksToolbar } from '../MyPacks/MyPacksToolbar';
import { MyPacksFooter } from '../MyPacks/MyPacksFooter';
import { ShipDisclaimerModal } from '../MyPacks/ShipDisclaimerModal';
import { SellingOverlay } from '../MyPacks/SellingOverlay';
import { RepackzTab } from '../MyPacks/tabs/RepackzTab';
import { DropsTab } from '../MyPacks/tabs/DropsTab';
import { DeliveriesTab } from '../MyPacks/tabs/DeliveriesTab';
import { DeliveryNewPage, DeliveryShowPage } from '../Delivery';
import { useShippingRoute } from './shippingRoute';
import { useShippingInventory } from './useShippingInventory';

const TABS: MyPacksTabDef[] = [
  { id: 'repackz', label: 'Packs' },
  { id: 'drops', label: 'Cards' },
  { id: 'deliveries', label: 'Shipping' },
];

// Background poll for the active tab, mirroring MyPacksPage: keeps a long-lived
// focused tab from drifting stale (e.g. a card sold in another browser tab).
const REFETCH_INTERVAL_MS = 30_000;

// Public query param name the parent platform opens `/shipping?with_navigation`
// with, e.g. `?active_tab=cards`. Values are the tab labels the parent sees
// (lowercased), not the internal `MyPacksTabId`s, which mirror backend
// terminology instead.
const ACTIVE_TAB_PARAM = 'active_tab';
const ACTIVE_TAB_PARAM_VALUES: Record<string, MyPacksTabId> = {
  packs: 'repackz',
  cards: 'drops',
  shipping: 'deliveries',
};

/**
 * Initial tab for the standalone `/shipping?with_navigation` hub, from
 * `?active_tab=packs|cards|shipping`. Unset/unrecognised falls back to
 * `deliveries` (the page's original default — it's what `/shipping` is for).
 * Read once at mount, same as `with_navigation` in ShippingInner: the parent
 * picks the tab when it opens the iframe, so it isn't re-read after that.
 */
function readInitialTab(): MyPacksTabId {
  if (typeof window === 'undefined') return 'deliveries';
  const raw = new URLSearchParams(window.location.search).get(ACTIVE_TAB_PARAM);
  return (raw ? ACTIVE_TAB_PARAM_VALUES[raw] : undefined) ?? 'deliveries';
}

/**
 * Leave the standalone Shipping page for the game, optionally deep-linking to a
 * pack (`game_id`) and its pending reveal (`order_id`) — both params are read by
 * PackPage on mount, so this lands exactly where the in-game CTA would.
 *
 * App.tsx picks the top-level route from window.location.pathname once per
 * render, so an SPA pushState would not remount the game — this has to be a real
 * document navigation. The rest of the query string (theme params, embed_key, …)
 * is carried over; the session token lives in sessionStorage and survives the
 * reload.
 */
function navigateToGame(params?: { gameId: string; orderId: string }): void {
  const url = new URL(window.location.href);
  url.pathname = '/';
  if (params) {
    url.searchParams.set('game_id', params.gameId);
    url.searchParams.set('order_id', params.orderId);
  }
  window.location.assign(url);
}

function goHome(): void {
  navigateToGame();
}

// "Open Now" on a Packs row — jump into the game's reveal for that pack's
// oldest unopened order. Offers with no pending order have nothing to reveal.
function openPackInGame(offer: IframeUnopenedOffer): void {
  if (!offer.firstUnopenedOrderId) return;
  navigateToGame({ gameId: String(offer.id), orderId: offer.firstUnopenedOrderId });
}

export interface ShippingTabsPageProps {
  client: ApiClient;
}

/**
 * `/shipping?with_navigation` — the full My Packs tab hub (Packs / Cards /
 * Shipping) mounted on the standalone Shipping route. Same UI as MyPacksPage
 * but with no Home button (nowhere to return to) and the delivery sub-flow
 * rooted at `/shipping` via `useShippingRoute`. Defaults to the Shipping tab,
 * or to `?active_tab=packs|cards|shipping` when the parent sets one.
 */
export function ShippingTabsPage({ client }: ShippingTabsPageProps) {
  const [tab, setTab] = useState<MyPacksTabId>(readInitialTab);
  const [shipModalOpen, setShipModalOpen] = useState(false);
  const { route, push: navigate } = useShippingRoute();
  const disableShipping = useShippingDisabled();

  const {
    repackz,
    drops,
    deliveries,
    selected,
    setSelected,
    selectedItems,
    selectableIds,
    submitting,
    handleItemToggle,
    handleItemSell,
    handleSubmit,
    refetchAfterSell,
  } = useShippingInventory(client);

  // Hide the Shipping tab when shipping is disabled, and guard against a stale
  // `tab` landing on the deliveries panel.
  const visibleTabs = disableShipping ? TABS.filter((t) => t.id !== 'deliveries') : TABS;
  const activeTab = disableShipping && tab === 'deliveries' ? 'drops' : tab;

  // Block deep-links to the create-delivery route when shipping is disabled.
  useEffect(() => {
    if (disableShipping && route.kind === 'delivery-new') {
      navigate({ kind: 'index' });
    }
  }, [disableShipping, route.kind, navigate]);

  // Host back button (modules/backStack): a delivery sub-route steps back to
  // the index, same as its own Back button.
  useBackHandler(route.kind !== 'index', () => navigate({ kind: 'index' }), 'subroute');

  // Reload the visited tab's feed on every tab switch (the mount run is skipped
  // — each hook bootstraps itself). The backend settles mutations async, so a
  // tab revisit must re-request; stale-while-revalidate hides the reload.
  const repackzRefetch = repackz.refetch;
  const dropsRefetch = drops.refetch;
  const deliveriesRefetch = deliveries.refetch;
  const refetchActiveTab = useCallback(() => {
    if (activeTab === 'repackz') void repackzRefetch();
    else if (activeTab === 'drops') void dropsRefetch();
    else if (activeTab === 'deliveries') void deliveriesRefetch();
  }, [activeTab, repackzRefetch, dropsRefetch, deliveriesRefetch]);

  const skipMountRefetchRef = useRef(true);
  useEffect(() => {
    if (skipMountRefetchRef.current) {
      skipMountRefetchRef.current = false;
      return;
    }
    refetchActiveTab();
  }, [refetchActiveTab]);

  // Keep the active tab fresh so a card sold in another browser tab disappears
  // here instead of keeping a stale buyback pill the backend would reject.
  // Both triggers are stale-while-revalidate and scoped to a visible page, so
  // idle background tabs never poll.
  useEffect(() => {
    const refetchIfVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refetchActiveTab();
    };
    document.addEventListener('visibilitychange', refetchIfVisible);
    const intervalId = setInterval(refetchIfVisible, REFETCH_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', refetchIfVisible);
      clearInterval(intervalId);
    };
  }, [refetchActiveTab]);

  if (route.kind === 'delivery-new' && !disableShipping) {
    return (
      <DeliveryNewPage
        client={client}
        selectedItems={selectedItems}
        onBack={() => navigate({ kind: 'index' })}
        onSuccess={() => {
          // Shipping created — back to the list and pivot to the Shipping tab.
          // Refetch its feed (hook lives above, no remount) and Cards (shipped
          // cards are no longer sellable and must drop their buyback pills).
          setSelected(new Set());
          setTab('deliveries');
          void deliveries.refetch();
          refetchAfterSell();
          navigate({ kind: 'index' });
        }}
      />
    );
  }

  if (route.kind === 'delivery-show') {
    return (
      <DeliveryShowPage
        client={client}
        shippingId={route.id}
        onBack={() => navigate({ kind: 'index' })}
      />
    );
  }

  return (
    <div data-page="my-packs">
      <Backdrop bgImageUrl={null} />
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          backgroundImage: 'var(--co-MyPacksPageBackground-bgImage, none)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      <div
        data-co-component="MyPacksPageBackground"
        className="@container relative flex min-h-screen w-full flex-col"
      >
        <MyPacksHeader
          tabs={visibleTabs}
          active={activeTab}
          onChange={(id) => {
            window.scrollTo(0, 0);
            setTab(id);
          }}
          {...(!disableShipping && { onAddDelivery: () => navigate({ kind: 'delivery-new' }) })}
        />

        <div className="mx-auto flex w-full max-w-350 flex-1 flex-col">
          {activeTab === 'drops' && (
            <div
              data-co-component="MyPacksStatsBar"
              className="
                mx-4 my-4
                flex flex-col gap-3
                @[600px]:mx-6 @[600px]:py-3 @[600px]:px-4
                @[600px]:[border-radius:var(--co-MyPacksStatsBar-borderRadius,24px)]
                @[600px]:[border:var(--co-MyPacksStatsBar-borderWidth,1px)_solid_var(--co-MyPacksStatsBar-borderColor,rgba(242,242,242,0.10))]
                @[900px]:h-[52px] @[900px]:flex-row
                @[900px]:items-center @[900px]:justify-between
                @[900px]:gap-3 @[900px]:[border-radius:var(--co-MyPacksStatsBar-borderRadius,40px)] @[900px]:py-0 @[900px]:pl-4 @[900px]:pr-2
              "
              style={{ background: 'var(--co-MyPacksStatsBar-fill, transparent)' }}
            >
              <MyPacksStatsRow inventory={drops.items} packsOpened={drops.totalCount} />
              <MyPacksToolbar
                totalSelectable={selectableIds.length}
                selectedCount={selected.size}
                onSelectAll={() => setSelected(new Set(selectableIds))}
                onClear={() => setSelected(new Set())}
                onShip={() => {
                  if (selected.size === 0) return;
                  setShipModalOpen(true);
                }}
                onSubmit={handleSubmit}
                submitting={submitting}
                canShip={!disableShipping}
              />
            </div>
          )}

          <main
            className={
              'flex-1 px-4 @[600px]:px-6 @[600px]:pb-12 ' +
              (activeTab === 'drops' ? 'pb-32' : 'pb-12')
            }
          >
            {activeTab === 'repackz' && (
              <RepackzTab
                offers={repackz.items}
                initialLoading={repackz.initialLoading}
                loadingMore={repackz.loadingMore}
                reachedEnd={repackz.reachedEnd}
                sentinelRef={repackz.sentinelRef}
                onOpen={openPackInGame}
                onBrowse={goHome}
              />
            )}
            {activeTab === 'drops' && (
              <DropsTab
                items={drops.items}
                initialLoading={drops.initialLoading}
                loadingMore={drops.loadingMore}
                reachedEnd={drops.reachedEnd}
                sentinelRef={drops.sentinelRef}
                onBrowse={goHome}
                onItemClick={handleItemToggle}
                onItemSell={handleItemSell}
                selectedIds={selected}
                selling={submitting}
              />
            )}
            {activeTab === 'deliveries' && (
              <DeliveriesTab
                shippings={deliveries.items}
                initialLoading={deliveries.initialLoading}
                loadingMore={deliveries.loadingMore}
                reachedEnd={deliveries.reachedEnd}
                error={deliveries.error}
                onRetry={() => void deliveries.refetch()}
                sentinelRef={deliveries.sentinelRef}
                onOpenDelivery={(id) => navigate({ kind: 'delivery-show', id })}
              />
            )}
          </main>

          <div className={activeTab === 'drops' ? 'pb-20 @[600px]:pb-0' : ''}>
            <MyPacksFooter client={client} />
          </div>
        </div>
      </div>

      <SellingOverlay visible={submitting} />

      {shipModalOpen && (
        <ShipDisclaimerModal
          client={client}
          poolItemIds={[...selected]}
          onClose={() => setShipModalOpen(false)}
          onAcceptBuyback={() => {
            setShipModalOpen(false);
            void handleSubmit();
          }}
          onAddDelivery={() => {
            setShipModalOpen(false);
            navigate({ kind: 'delivery-new' });
          }}
        />
      )}
    </div>
  );
}
